import classNames from "classnames";
import { DragEvent, Fragment, PropsWithChildren, ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SWRMutationResponse } from "swr/mutation";
import { UseListFiltersReturn } from "../../hooks/useListFilters";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { useI18n } from "../../hooks/useTranslations";
import { CustomError, getErrorMessage } from "../../http/errors";
import { ColumnFilterValue, Glyph, MenuOptionValue, StoredColumnFilterValue } from "../../types";
import ArrowDown from "../icons/ArrowDown";
import { default as ArrowDropDown, default as SvgArrowDropDown } from "../icons/ArrowDropDown";
import ArrowUp from "../icons/ArrowUp";
import SvgEdit from "../icons/Edit";
import Filter from "../icons/Filter";
import SvgNavigateNext from "../icons/NagivateNext";
import SvgNavigateBefore from "../icons/NavigateBefore";
import Button from "./Button";
import { ColumnFilter } from "./columnFilters/types";
import ErrorText from "./ErrorText";
import HeaderMenu from "./HeaderMenu";
import Icon from "./Icon";
import Input from "./Input";
import ListItemMenu, { Action } from "./ListItemMenu";
import LoadingSpinner from "./LoadingSpinner";
import SearchBar from "./SearchBar";
import styles from "./Table.module.css";
import TableFileUploadDialog, { type TableFileUploadResult, type TableUploadFileResult } from "./TableFileUploadDialog";

export type TableViews = {
  title: string;
  items: {
    label: string;
    url: string;
  }[];
  maxWidth?: `${number}px`;
};

interface Props<T> {
  className?: string;
  filters?: UseListFiltersReturn;
  searchOptions?: {
    disabled?: boolean;
    placeholder?: string;
    onChange: () => void;
  };
  total?: number;
  head?: ReactNode;
  onRowClick?: (item: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  items?: T[];
  getRowStyle?: (item: T) => React.CSSProperties | undefined;
  renderRow: (item: T, index: number) => ReactNode;
  rowActions?: (item: T) => Action[];
  listActions?: {
    label: string;
    onClick: () => void;
    icon?: Glyph;
  }[];
  footer?: ReactNode;
  isTall?: boolean;
  buttons?: {
    main?: ReactNode | ReactNode[];
    secondary?: ReactNode | ReactNode[];
  };
  fileDrop?: {
    enabled: boolean;
    onDrop?: (files: File[]) => void | Promise<void>;
    onUploadFile?: (file: File) => Promise<TableUploadFileResult>;
    isLoading?: boolean;
    isDraggingOn?: (isDragging: boolean) => void;
  } | null;
  error?: CustomError;
  views?: TableViews;
  hoverTr?: boolean;
  // the table fits the container width (e.g. a form field in a dialog) wrapping long texts, instead of growing to its content
  fitWidth?: boolean;
  selectable?: {
    onRowSelect: (item: T, selected: boolean) => void;
    isRowSelected: (item: T) => boolean;
    isRowSelectable: (item: T) => boolean;
    selectAll?: {
      selected?: boolean;
      toggle: () => void;
    };
  };
}

const Table = <T,>({
  className,
  filters,
  searchOptions,
  total = -1,
  head,
  items,
  getRowStyle,
  renderRow,
  rowActions,
  listActions,
  footer,
  isTall = false,
  buttons,
  fileDrop,
  error,
  views,
  hoverTr = true,
  onRowClick,
  fitWidth = false,
  selectable,
}: Props<T>) => {
  const i18n = useI18n();

  const isSearchable = filters?.isSearchable && searchOptions;
  const page = filters?.state.page ?? 1;
  const limit = filters?.state.limit ?? 5;

  const fromItem = (page - 1) * limit + 1;
  const maxToItem = fromItem + limit - 1;

  const [data, setData] = useState<{ items: T[]; total: number }>();

  const [showViewsMenu, setShowViewsMenu] = useState(false);
  const viewsMenuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(viewsMenuRef, () => setShowViewsMenu(false));

  const [showListActionsMenu, setShowListActionsMenu] = useState(false);
  const listActionsMenuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(listActionsMenuRef, () => setShowListActionsMenu(false));

  const mainButtons = buttons?.main ? (Array.isArray(buttons.main) ? buttons.main : [buttons.main]) : [];
  const secondaryButtons = buttons?.secondary
    ? Array.isArray(buttons.secondary)
      ? buttons.secondary
      : [buttons.secondary]
    : [];
  const hasToolbar =
    isSearchable || mainButtons.length > 0 || secondaryButtons.length > 0 || !!views || !!listActions?.length;

  useEffect(() => {
    !!items && setData({ items, total });
  }, [items]);

  const toItem = Math.max(Math.min(maxToItem, data?.total ?? 0), 0);

  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showFileUploadDialog, setShowFileUploadDialog] = useState(false);
  const [isUploadingDroppedFiles, setIsUploadingDroppedFiles] = useState(false);
  const [fileUploadResults, setFileUploadResults] = useState<TableFileUploadResult[]>([]);

  const fileDropConfigured = !!fileDrop?.enabled && (!!fileDrop?.onDrop || !!fileDrop?.onUploadFile);
  const isFileDropBusy = !!fileDrop?.isLoading || isUploadingDroppedFiles;

  const hasDraggedFiles = (event: DragEvent<HTMLElement>) => Array.from(event.dataTransfer.types).includes("Files");

  const onFileDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!fileDropConfigured || !hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();

    if (!isFileDropBusy) {
      event.dataTransfer.dropEffect = "copy";
      fileDrop?.isDraggingOn?.(true);
    }
  };

  const onFileDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!fileDropConfigured || !hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();

    if (!isFileDropBusy) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  const onFileDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!fileDropConfigured) {
      return;
    }

    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    fileDrop?.isDraggingOn?.(false);
  };

  const onFileDrop = async (event: DragEvent<HTMLDivElement>) => {
    if (!fileDropConfigured || !hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();

    if (isFileDropBusy) {
      return;
    }

    const files = Array.from(event.dataTransfer.files);
    if (!files.length) {
      return;
    }

    fileDrop?.isDraggingOn?.(false);

    if (!fileDrop?.onUploadFile) {
      await fileDrop?.onDrop?.(files);
      return;
    }

    const nextResults: TableFileUploadResult[] = files.map((file) => ({
      fileName: file.name,
      status: "pending",
    }));

    setFileUploadResults([...nextResults]);
    setShowFileUploadDialog(true);
    setIsUploadingDroppedFiles(true);

    try {
      for (const [index, file] of files.entries()) {
        nextResults[index] = {
          fileName: file.name,
          status: "uploading",
        };
        setFileUploadResults([...nextResults]);

        try {
          const status = await fileDrop.onUploadFile(file);
          nextResults[index] = {
            fileName: file.name,
            status: status ?? "success",
          };
        } catch (error) {
          nextResults[index] = {
            fileName: file.name,
            status: "error",
            errorMessage: getErrorMessage(i18n, error),
          };
        }

        setFileUploadResults([...nextResults]);
      }
    } finally {
      setIsUploadingDroppedFiles(false);
    }

    return nextResults;
  };

  const tableContent = (
    <>
      {hasToolbar && (
        <div className={styles.toolbar}>
          <div className={styles.toolbarPrimary}>
            {mainButtons.map((x, i) => (
              <Fragment key={`button_${i}`}>{x}</Fragment>
            ))}
            {isSearchable && (
              <SearchBar
                className={styles.search}
                placeholder={searchOptions?.placeholder ?? i18n.t.common.search}
                value={filters.state.q ?? ""}
                onChange={(term) => {
                  if (searchOptions && !searchOptions?.disabled) {
                    searchOptions.onChange();
                    filters.setSearchTerm(term);
                  }
                }}
              />
            )}
          </div>

          <div className={styles.toolbarSecondary}>
            {!!views && (
              <div className={styles.menuContainer} ref={viewsMenuRef}>
                <Button
                  buttonProps={{ onClick: () => setShowViewsMenu(!showViewsMenu) }}
                  glyph={SvgArrowDropDown}
                  maxWidth={views.maxWidth}
                >
                  {views.title}
                </Button>
                <div className={classNames(styles.menu, showViewsMenu ? styles.visible : undefined)}>
                  {views.items.map((view, index) => (
                    <Link
                      key={`v_${index}`}
                      to={view.url}
                      onClick={() => setShowViewsMenu(false)}
                      className={classNames(styles.menuLink, styles.menuItem)}
                    >
                      {view.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {secondaryButtons.map((x, i) => (
              <Fragment key={`secondary_button_${i}`}>{x}</Fragment>
            ))}

            {!!listActions?.length && (
              <div className={styles.menuContainer} ref={listActionsMenuRef}>
                <Button
                  glyph={SvgArrowDropDown}
                  buttonProps={{ onClick: () => setShowListActionsMenu(!showListActionsMenu) }}
                />
                <div className={classNames(styles.menu, showListActionsMenu ? styles.visible : undefined)}>
                  {listActions.map((x, index) => (
                    <button
                      key={`action_${index}`}
                      type="button"
                      className={styles.menuAction}
                      onClick={(e) => {
                        e.preventDefault();
                        x.onClick();
                        setShowListActionsMenu(false);
                      }}
                    >
                      {x.icon && <Icon glyph={x.icon} className={styles.icon} />}
                      {x.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={classNames(styles.tableShell, { [styles.fitWidth]: fitWidth })}>
        <table
          className={classNames(className, styles.table, {
            [styles.tall]: isTall,
          })}
        >
          {head && (
            <thead>
              <tr>
                {selectable?.selectAll ? (
                  <th align="left" className={styles.selectAllHeader}>
                    <input
                      type="checkbox"
                      checked={selectable?.selectAll.selected && !!selectable.isRowSelectable}
                      onChange={() => selectable?.selectAll?.toggle()}
                    />
                  </th>
                ) : undefined}
                {head}
              </tr>
            </thead>
          )}
          {!fileDrop?.isLoading && !error && !!data?.items && (
            <>
              <tbody>
                {data?.items?.map((item, index) => {
                  return (
                    <tr
                      key={`table_row_${index}`}
                      onClick={
                        onRowClick
                          ? (event) => {
                              const selection = window.getSelection();
                              const selectedText = selection?.toString();
                              if (selectedText?.length) {
                                return;
                              }
                              clickTimeoutRef.current = setTimeout(() => {
                                onRowClick(item, event);
                              }, 200);
                            }
                          : undefined
                      }
                      onDoubleClick={
                        onRowClick
                          ? () => {
                              if (clickTimeoutRef.current) {
                                clearTimeout(clickTimeoutRef.current);
                                clickTimeoutRef.current = null;
                              }
                            }
                          : undefined
                      }
                      style={getRowStyle?.(item)}
                      className={classNames({ [styles.clickable]: !!onRowClick, [styles.hover]: hoverTr })}
                    >
                      {!!selectable && (
                        <td
                          className={styles.selectionCell}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!(e.target instanceof HTMLInputElement)) {
                              const checkbox = e.currentTarget.firstElementChild as HTMLInputElement;
                              checkbox.click();
                            }
                          }}
                        >
                          {selectable.isRowSelectable(item) && (
                            <input
                              type="checkbox"
                              checked={selectable.isRowSelected(item)}
                              onChange={(e) => selectable.onRowSelect(item, e.target.checked)}
                            />
                          )}
                        </td>
                      )}
                      {renderRow?.(item, index)}
                      {rowActions ? (
                        <td
                          width={1}
                          className={styles.noPadding}
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                        >
                          <ListItemMenu item={item} actions={rowActions(item)} />
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
              {footer && <tfoot>{footer}</tfoot>}
            </>
          )}
        </table>
      </div>

      {!error && !!data?.items && data.total === 0 && (
        <div className={styles.noItemsFound}>
          <div>
            <img src="/no_items_found.webp" />
          </div>
          <div>{i18n.t.common.noItemsFound}</div>
        </div>
      )}

      {(fileDrop?.isLoading || (!error && !data?.items)) && (
        <div className={styles.loadingSpinner}>
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className={styles.errorLoadingData}>
          <ErrorText text={i18n.t.common.errorLoadingData} />
        </div>
      )}

      {!error && !!data?.items && data.total > 0 && (
        <div className={styles.controls}>
          <div>
            {fromItem} - {toItem} of {data.total}
          </div>
          {data.total > limit && (
            <div className={styles.pagination}>
              <Button
                glyph={SvgNavigateBefore}
                buttonProps={{
                  title: i18n.t.common.previousNRows(limit),
                  disabled: page === 1,
                  onClick: () => filters?.prevPage(),
                  className: styles.paginationButton,
                }}
              />
              <Button
                glyph={SvgNavigateNext}
                buttonProps={{
                  title: i18n.t.common.nextNRows(limit),
                  disabled: toItem === data.total,
                  onClick: () => filters?.nextPage(),
                  className: styles.paginationButton,
                }}
              />
            </div>
          )}
        </div>
      )}

      {showFileUploadDialog && (
        <TableFileUploadDialog
          isUploading={isUploadingDroppedFiles}
          onClose={() => setShowFileUploadDialog(false)}
          results={fileUploadResults}
        />
      )}
    </>
  );

  if (!fileDropConfigured) {
    return tableContent;
  }

  return (
    <div
      className={classNames(styles.fileDropZone, {
        [styles.fileDropZoneLoading]: isFileDropBusy,
      })}
      onDragEnter={onFileDragEnter}
      onDragOver={onFileDragOver}
      onDragLeave={onFileDragLeave}
      onDrop={onFileDrop}
    >
      {tableContent}
    </div>
  );
};

type TdProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  noWrap?: boolean;
  error?: boolean;
};

const Td = ({ noWrap, error, ...rest }: TdProps) => (
  <td
    {...rest}
    className={classNames({
      [styles.noWrap]: noWrap,
      [styles.error]: error,
    })}
  ></td>
);

Table.Td = Td;

const IconTd = ({ children }: PropsWithChildren) => (
  <td className={styles.iconTd}>
    <div>{children}</div>
  </td>
);

Table.IconTd = IconTd;

type THProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  title: string;
  fieldName: string;
  order?: {
    by?: string;
    setBy: (fieldName: string, desc: boolean) => void;
    desc: boolean;
  };
  filters?: {
    getSelected: (fieldName: string) => StoredColumnFilterValue;
    update: (fieldName: string, value: ColumnFilterValue) => void;
    get: (
      fieldName: string,
      signal: AbortSignal,
    ) => SWRMutationResponse<MenuOptionValue[] | null, CustomError, string, never>;
  };
  filter?: ColumnFilter;
  formatter?: (value: MenuOptionValue) => string | undefined;
};

const Th = ({ fieldName, order, filters, filter, formatter, ...rest }: THProps) => {
  const [show, setShow] = useState(false);
  const thRef = useRef<HTMLTableCellElement>(null);

  const toggleOrder = (event: React.MouseEvent) => {
    if (order) {
      const desc = order.by == fieldName ? !order.desc : false;
      order.setBy(fieldName, desc);
      event.stopPropagation();
    }
  };

  const onClose = () => setShow(false);

  const openMenu = (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
    if (order || filters) {
      event.stopPropagation();
      setShow(true);
    }
  };

  const setOrder = (desc: boolean) =>
    (desc !== order?.desc || order?.by !== fieldName) && order?.setBy?.(fieldName, desc);

  const selected = filters?.getSelected(fieldName) ?? { values: [] };
  const hasActiveFilter = selected.values.length > 0 || !!selected.operator;

  return (
    <th {...rest} onClick={openMenu} ref={thRef}>
      <div className={styles.content}>
        {order || filters ? (
          <span className={styles.text} onClick={toggleOrder}>
            {rest.title}
          </span>
        ) : (
          <span className={styles.text}>{rest.title}</span>
        )}
        {order?.by == fieldName ? order.desc ? <ArrowDown /> : <ArrowUp /> : undefined}
        {hasActiveFilter && <Filter />}

        {(filters || order) && (
          <div className={styles.dropDown}>
            <ArrowDropDown />
          </div>
        )}
      </div>

      <HeaderMenu
        show={show}
        anchorEl={thRef.current}
        alignright={rest.align === "right"}
        fieldName={fieldName}
        title={rest.title}
        onClose={onClose}
        setOrder={order ? (order) => setOrder(order === "DESC") : undefined}
        filters={filters}
        filter={filter}
        formatter={formatter}
      />
    </th>
  );
};

Table.Th = Th;

type EditableCellProps = PropsWithChildren & {
  defaultName: string;
  itemId: string;
  onSubmit: (id: string, name: string, callback: () => void) => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  disabled?: boolean;
};

const EditableCell = ({
  children,
  itemId,
  defaultName,
  onSubmit,
  onCancel,
  isEditing,
  disabled,
}: EditableCellProps) => {
  const i18n = useI18n();

  const splitFilename = (name: string) => {
    const lastDot = name.lastIndexOf(".");
    if (lastDot <= 0) return { base: name, ext: "" };
    return {
      base: name.slice(0, lastDot),
      ext: name.slice(lastDot + 1),
    };
  };

  const { base, ext } = splitFilename(defaultName);
  const [itemToEdit, setItemToEdit] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [width, setWidth] = useState(0);

  const tdRef = useRef<HTMLTableCellElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    if (disabled) return;

    if (tdRef.current) {
      setWidth(tdRef.current.offsetWidth);
    }

    setItemToEdit(base);
  };

  const cancelEditing = () => {
    setItemToEdit(null);
    onCancel();
  };

  const submit = async () => {
    if (itemToEdit === null || isSubmitting) {
      cancelEditing();
      return;
    }

    const trimmedName = itemToEdit.trim();

    if (!trimmedName) {
      cancelEditing();
      return;
    }

    if (trimmedName !== base) {
      setIsSubmitting(true);

      const newName = ext ? `${trimmedName}.${ext}` : trimmedName;

      await onSubmit(itemId, newName, () => setItemToEdit(null));

      setIsSubmitting(false);
    } else {
      setItemToEdit(null);
    }
  };

  useEffect(() => {
    if (disabled) return;

    if (isEditing) {
      startEditing();
    } else if (itemToEdit !== null) {
      setItemToEdit(null);
    }
  }, [isEditing]);

  useOnClickOutside(inputRef, () => submit());

  if (isSubmitting) {
    return <td>{i18n.t.common.saving}...</td>;
  }

  if (itemToEdit !== null) {
    return (
      <td className={styles.noPadding} style={{ width }}>
        <Input
          value={itemToEdit}
          autoFocus
          ref={inputRef}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setItemToEdit(e.currentTarget.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              submit();
              return;
            }

            if (e.key === "Escape") {
              cancelEditing();
              return;
            }
          }}
        />
      </td>
    );
  }

  return (
    <td ref={tdRef}>
      <div className={styles.columnEditable}>
        <div>{children ? children : defaultName}</div>
        {!disabled && <Icon glyph={SvgEdit} className={styles.iconEditable} onClick={startEditing} />}
      </div>
    </td>
  );
};

Table.EditableCell = EditableCell;

export default Table;
